// ── Capture-time timestamp watermark (feature #3 — Zeitstempel im Bild) ──────
// Deliberately small + unobtrusive: ~1.4% of image width, faint pill in the
// bottom-right. Crisp when zooming in (documentation value), not an eye-catcher
// on thumbnails.
//
// Lives in its own module (no DOM/Capacitor/i18n imports) so the image-compress
// WORKER can import it — the stamp is drawn inside the worker on the already-
// downscaled canvas, not on a full-res main-thread canvas. See `drawStamp`.

/** 2D context shape shared by <canvas> and OffscreenCanvas. */
type StampContext = Pick<
  CanvasRenderingContext2D,
  'font' | 'textBaseline' | 'textAlign' | 'fillStyle' | 'measureText' | 'fillRect' | 'fillText'
>

/** German capture-time label, e.g. "19.07.2026, 14:32". */
export function formatStampText(date: Date = new Date()): string {
  return date.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

/**
 * Draw the timestamp pill into the bottom-right of a `w`×`h` canvas.
 *
 * Call this on the FINAL (already resized) canvas — sizing is relative to the
 * canvas width, so stamping before a downscale would shrink the text with it.
 */
export function drawStamp(ctx: StampContext, w: number, h: number, text: string): void {
  const fontSize = Math.max(11, Math.round(w * 0.014))
  const pad = Math.round(fontSize * 0.5)
  ctx.font = `500 ${fontSize}px -apple-system, "Segoe UI", Roboto, Arial, sans-serif`
  ctx.textBaseline = 'bottom'
  ctx.textAlign = 'right'
  const tw = ctx.measureText(text).width
  const x = w - pad
  const y = h - pad
  ctx.fillStyle = 'rgba(0,0,0,0.35)'
  ctx.fillRect(x - tw - pad * 0.6, y - fontSize - pad * 0.3, tw + pad * 1.2, fontSize + pad * 0.6)
  ctx.fillStyle = 'rgba(255,255,255,0.92)'
  ctx.fillText(text, x, y)
}
